import { COUPON_VALIDATION, DISCOUNT_TYPES, ZERO } from "constants/coupons";
import {
  createEmptyCurrencySettingsEntry,
  isFlatDiscount,
  isPercentageDiscount,
  normalizeDiscountType,
} from "helpers/couponDiscountType";
import {
  normalizeTenantIdList,
  resolveTenantCurrencyIds,
} from "helpers/couponScopeOptions";
import { parsePackageIds } from "helpers/packageIds";

const API_DISCOUNT_TYPES = {
  FLAT: "FLAT",
  PERCENTAGE: "PERCENTAGE",
};

export const resolveCouponCodeFromApi = (coupon) =>
  String(coupon.coupon_code ?? coupon.code ?? coupon.couponCode ?? "").trim();

const toFormDecimalString = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  return String(value);
};

const readPackageSelection = (packageIds = []) => {
  const selectAllPackages = packageIds.includes(
    COUPON_VALIDATION.ALL_PACKAGES_ID
  );
  const filteredPackageIds = packageIds.filter(
    (packageId) => packageId !== COUPON_VALIDATION.ALL_PACKAGES_ID
  );

  return {
    selectAllPackages,
    packageIds: selectAllPackages ? [] : filteredPackageIds,
    packagesCount: selectAllPackages ? undefined : filteredPackageIds.length,
  };
};

const readPlatformSelection = (platformIds = []) => {
  const selectAllPlatforms = platformIds.includes(
    COUPON_VALIDATION.ALL_PLATFORMS_ID
  );
  const filteredPlatformIds = platformIds.filter(
    (platformId) => platformId !== COUPON_VALIDATION.ALL_PLATFORMS_ID
  );

  return {
    selectAllPlatforms,
    platformIds: selectAllPlatforms ? [] : filteredPlatformIds,
  };
};

const toDecimalString = (value) => {
  if (value === "" || value === null || value === undefined) {
    return String(COUPON_VALIDATION.MIN_MRP_AMOUNT);
  }

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return String(COUPON_VALIDATION.MIN_MRP_AMOUNT);
  }

  return String(numericValue);
};

const resolveMinimumCartMrpForApi = (value) => {
  if (value === "" || value === null || value === undefined) {
    return COUPON_VALIDATION.MIN_MRP_AMOUNT;
  }

  const minimumCartMrp = Number(value);
  return Number.isNaN(minimumCartMrp)
    ? COUPON_VALIDATION.MIN_MRP_AMOUNT
    : minimumCartMrp;
};

export const normalizeDiscountTypeForApi = (discountType) => {
  const normalizedDiscountType = normalizeDiscountType(discountType);

  if (normalizedDiscountType === DISCOUNT_TYPES.FLAT) {
    return API_DISCOUNT_TYPES.FLAT;
  }

  if (normalizedDiscountType === DISCOUNT_TYPES.PERCENTAGE) {
    return API_DISCOUNT_TYPES.PERCENTAGE;
  }

  return null;
};

export const fromApiDate = (apiDate) => {
  if (!apiDate) {
    return "";
  }

  const normalizedDate = String(apiDate).trim();

  if (normalizedDate.includes("/")) {
    const [day, month, year] = normalizedDate.split("/");
    if (!day || !month || !year) {
      return "";
    }

    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }

  return normalizedDate;
};

export const toApiDate = (isoDate) => {
  if (!isoDate) {
    return "";
  }

  const normalizedDate = String(isoDate).trim();

  if (normalizedDate.includes("/")) {
    return fromApiDate(normalizedDate);
  }

  const [year, month, day] = normalizedDate.split("-");
  if (!year || !month || !day) {
    return "";
  }

  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
};

export const getLocalIsoDate = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const shiftIsoDate = (isoDate, days) => {
  const [year, month, day] = isoDate.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);
  return getLocalIsoDate(date);
};

export const getMinExpiryDate = (effectiveDate) =>
  shiftIsoDate(
    effectiveDate,
    COUPON_VALIDATION.MIN_EXPIRY_DATE_AFTER_EFFECTIVE_DAYS
  );

export const getMaxExpiryDate = (effectiveDate) =>
  shiftIsoDate(
    effectiveDate,
    COUPON_VALIDATION.MAX_EXPIRY_DATE_AFTER_EFFECTIVE_DAYS
  );

const parseScopeIdForApi = (value) => {
  const normalizedValue = String(value ?? "").trim();
  if (normalizedValue === "") {
    return normalizedValue;
  }

  const numericValue = Number(normalizedValue);
  if (
    Number.isFinite(numericValue) &&
    String(numericValue) === normalizedValue
  ) {
    return numericValue;
  }

  return normalizedValue;
};

export const groupSelectedTenantsByCurrency = (tenants, tenantIds) => {
  const selectedTenantIdSet = new Set(normalizeTenantIdList(tenantIds));
  const groups = new Map();

  tenants.forEach((tenant) => {
    const tenantId = String(tenant.id).trim();
    if (!selectedTenantIdSet.has(tenantId)) {
      return;
    }

    resolveTenantCurrencyIds(tenant).forEach((rawCurrencyId) => {
      const currencyId = String(rawCurrencyId).trim();
      if (currencyId === "") {
        return;
      }

      if (!groups.has(currencyId)) {
        groups.set(currencyId, {
          currencyId,
          currencyCode: String(tenant.currencyCode ?? "").trim(),
          tenantIds: [],
        });
      }

      const group = groups.get(currencyId);
      if (!group.tenantIds.includes(tenantId)) {
        group.tenantIds.push(tenantId);
      }
      if (!group.currencyCode && tenant.currencyCode) {
        group.currencyCode = String(tenant.currencyCode).trim();
      }
    });
  });

  return [...groups.values()];
};

const buildCurrencyAmountEntry = (
  currencyId,
  settings,
  {
    includeMaxAllowed = false,
    includeDiscountAmount = false,
    includeDiscountPercentage = false,
    discountPercentage,
  } = {}
) => {
  const entry = {
    currency_id: parseScopeIdForApi(currencyId),
    minimum_package_mrp: toDecimalString(settings.minimumPackageMrp ?? ZERO),
    minimum_cart_mrp: toDecimalString(
      resolveMinimumCartMrpForApi(settings.minimumCartMrp)
    ),
  };

  if (includeMaxAllowed) {
    entry.max_allowed_discount_amount = toDecimalString(
      settings.maxAllowedAmount
    );
  }

  if (includeDiscountAmount) {
    entry.discount_amount = toDecimalString(settings.discountAmount);
  }

  if (includeDiscountPercentage) {
    entry.discount_percentage = toDecimalString(
      discountPercentage ?? settings.discountAmount
    );
  }

  return entry;
};

const buildUsagePayloadForCreate = (values) => {
  const usage = {
    total_coupon_count: Number(values.totalCouponCount),
    is_applicable_multiple_times: Boolean(values.isApplicableMultipleTimes),
    is_applicable_for_single_user: Boolean(values.isApplicableForSingleUser),
  };

  if (values.isApplicableForSingleUser) {
    usage.uam_id = Number(values.uamId);
  }

  return usage;
};

const mapApplicablePackagesToApi = (values) => {
  if (values.selectAllPackages) {
    return {
      select_all: true,
      package_ids: [],
    };
  }

  return {
    select_all: false,
    package_ids: parsePackageIds(values.packageIds),
  };
};

const mapApplicablePlatformsToApi = (values) => ({
  select_all: Boolean(values.selectAllPlatforms),
  platform_ids: values.selectAllPlatforms
    ? []
    : parsePackageIds(values.platformIds),
});

const buildCreatePayloadBase = (values) => ({
  category: values.category.trim(),
  remarks: values.remarks.trim(),
  validity: {
    effective_date: toApiDate(values.effectiveDate),
    expiry_date: toApiDate(values.expiryDate),
  },
  usage: buildUsagePayloadForCreate(values),
  applicable_packages: mapApplicablePackagesToApi(values),
  applicable_platforms: mapApplicablePlatformsToApi(values),
});

const buildFlatCurrencyAmounts = (values, currencyGroups) =>
  currencyGroups.map((currencyGroup) => {
    const settings =
      values.currencySettings?.[String(currencyGroup.currencyId)] ?? {};
    return buildCurrencyAmountEntry(currencyGroup.currencyId, settings, {
      includeDiscountAmount: true,
    });
  });

const buildPercentageCurrencyAmounts = (values, currencyGroups) =>
  currencyGroups.map((currencyGroup) => {
    const settings =
      values.currencySettings?.[String(currencyGroup.currencyId)] ?? {};
    return buildCurrencyAmountEntry(currencyGroup.currencyId, settings, {
      includeMaxAllowed: true,
      includeDiscountPercentage: true,
      discountPercentage: values.discountValue,
    });
  });

export const buildCreateCouponPayload = (values, { tenants = [] } = {}) => {
  const currencyGroups = groupSelectedTenantsByCurrency(
    tenants,
    values.tenantIds
  );

  if (currencyGroups.length === ZERO) {
    return null;
  }

  const allTenantIds = [
    ...new Set(currencyGroups.flatMap((group) => group.tenantIds)),
  ];

  const basePayload = {
    ...buildCreatePayloadBase(values),
    code: String(values.code).trim().toUpperCase(),
    tenant_ids: normalizeTenantIdList(allTenantIds).map(parseScopeIdForApi),
  };

  if (isPercentageDiscount(values.discountType)) {
    return {
      ...basePayload,
      discount: {
        type: API_DISCOUNT_TYPES.PERCENTAGE,
      },
      currency_amounts: buildPercentageCurrencyAmounts(values, currencyGroups),
    };
  }

  if (isFlatDiscount(values.discountType)) {
    return {
      ...basePayload,
      discount: {
        type: API_DISCOUNT_TYPES.FLAT,
      },
      currency_amounts: buildFlatCurrencyAmounts(values, currencyGroups),
    };
  }

  return null;
};

const readTenantIdsFromCoupon = (coupon) => {
  if (Array.isArray(coupon.tenant_ids) && coupon.tenant_ids.length > ZERO) {
    return normalizeTenantIdList(coupon.tenant_ids);
  }

  if (Array.isArray(coupon.tenants) && coupon.tenants.length > ZERO) {
    return normalizeTenantIdList(
      coupon.tenants.map((tenant) => tenant.tenant_id)
    );
  }

  return [];
};

export const mapApiTenantsToOptions = (tenants = []) =>
  tenants.map((tenant) => ({
    id: String(tenant.tenant_id),
    label: tenant.tenant_name,
    currencyId: tenant.currency_id,
    currencyCode: tenant.currency_code,
  }));

const buildCurrencySettingsFromCurrencyAmounts = ({
  currencyAmounts = [],
  discountType,
  discountValue,
}) => {
  if (!Array.isArray(currencyAmounts) || currencyAmounts.length === ZERO) {
    return {};
  }

  return currencyAmounts.reduce((settingsByCurrency, amount) => {
    const currencyId = String(amount.currency_id ?? "").trim();
    if (currencyId === "") {
      return settingsByCurrency;
    }

    const settings = createEmptyCurrencySettingsEntry();

    if (isFlatDiscount(discountType)) {
      settings.discountAmount = toFormDecimalString(amount.discount_amount);
    }

    if (isPercentageDiscount(discountType)) {
      settings.maxAllowedAmount = toFormDecimalString(
        amount.max_allowed_discount_amount
      );
    }

    settings.minimumPackageMrp = toFormDecimalString(
      amount.minimum_package_mrp ?? ZERO
    );
    const minimumCartMrp = toFormDecimalString(amount.minimum_cart_mrp ?? ZERO);
    settings.minimumCartMrp =
      minimumCartMrp === "0" || minimumCartMrp === "0.00" ? "" : minimumCartMrp;

    settingsByCurrency[currencyId] = settings;
    return settingsByCurrency;
  }, {});
};

const readApplicablePackageIds = (coupon) => {
  const applicablePackages = coupon.applicable_packages;
  if (!applicablePackages) {
    return [];
  }

  if (applicablePackages.select_all) {
    return [COUPON_VALIDATION.ALL_PACKAGES_ID];
  }

  return applicablePackages.package_ids ?? [];
};

const readApplicablePlatformIds = (coupon) => {
  const applicablePlatforms = coupon.applicable_platforms;
  if (!applicablePlatforms) {
    return [];
  }

  if (applicablePlatforms.select_all) {
    return [COUPON_VALIDATION.ALL_PLATFORMS_ID];
  }

  return applicablePlatforms.platform_ids ?? [];
};

const readPercentageDiscountValue = (currencyAmounts = []) => {
  const amountWithPercentage = currencyAmounts.find(
    (amount) =>
      amount.discount_percentage !== undefined &&
      amount.discount_percentage !== null &&
      amount.discount_percentage !== ""
  );

  return toFormDecimalString(
    amountWithPercentage?.discount_percentage ??
      currencyAmounts[ZERO]?.discount_percentage
  );
};

export const mapCouponDetailsFromApi = (coupon) => {
  const couponCode = resolveCouponCodeFromApi(coupon);
  const discountType = normalizeDiscountType(coupon.discount?.type);
  const isPercentage = isPercentageDiscount(discountType);
  const packageSelection = readPackageSelection(readApplicablePackageIds(coupon));
  const platformSelection = readPlatformSelection(readApplicablePlatformIds(coupon));
  const currencyAmounts = coupon.currency_amounts ?? [];
  const usage = coupon.usage ?? {};
  const discountValue = isPercentage
    ? readPercentageDiscountValue(currencyAmounts)
    : "";

  const currencySettings = buildCurrencySettingsFromCurrencyAmounts({
    currencyAmounts,
    discountType,
    discountValue,
  });

  const primaryCurrencySettings =
    Object.values(currencySettings)[ZERO] ?? {};

  return {
    id: couponCode,
    verticalId: "",
    tenantIds: readTenantIdsFromCoupon(coupon),
    tenantDetails: mapApiTenantsToOptions(coupon.tenants ?? []),
    currencyId: "",
    currencyCode: "",
    currencySettings,
    code: couponCode,
    category: coupon.category ?? "",
    remarks: coupon.remarks ?? "",
    discountType,
    discountValue,
    effectiveDate: fromApiDate(coupon.validity?.effective_date),
    expiryDate: fromApiDate(coupon.validity?.expiry_date),
    totalCouponCount: usage.total_coupon_count ?? "",
    totalUsageLimit: usage.total_coupon_count ?? "",
    usedCount: usage.used_count ?? ZERO,
    isApplicableMultipleTimes: Boolean(usage.is_applicable_multiple_times),
    isApplicableForSingleUser: Boolean(usage.is_applicable_for_single_user),
    uamId: usage.uam_id ?? "",
    maxAllowedAmount: isPercentage
      ? primaryCurrencySettings.maxAllowedAmount ?? ""
      : "",
    selectAllPackages: packageSelection.selectAllPackages,
    packageIds: packageSelection.packageIds,
    packagesCount: packageSelection.packagesCount,
    selectAllPlatforms: platformSelection.selectAllPlatforms,
    platformIds: platformSelection.platformIds,
    createdOn: coupon.created_date ?? "",
    modifiedOn: coupon.modified_date ?? "",
    status: String(coupon.status ?? "").toLowerCase(),
  };
};

export const mapActiveCouponFromApi = (coupon) => {
  const couponCode = String(coupon.coupon_code ?? "").trim();

  return {
    id: couponCode,
    code: couponCode,
    category: coupon.coupon_category ?? "",
    discountType: normalizeDiscountType(coupon.discount?.type),
    effectiveDate: fromApiDate(coupon.validity?.effective_date),
    expiryDate: fromApiDate(coupon.validity?.expiry_date),
    totalUsageLimit: coupon.usage?.total_count ?? "",
    usedCount: coupon.usage?.used_count ?? ZERO,
    createdOn: coupon.created_date ?? "",
  };
};

const buildUsageUpdatePayload = (values, originalValues) => {
  const usage = {};
  const nextCount = Number(values.totalCouponCount);
  const previousCount = Number(originalValues.totalCouponCount);

  if (
    !Number.isNaN(nextCount) &&
    !Number.isNaN(previousCount) &&
    nextCount !== previousCount
  ) {
    usage.total_coupon_count = nextCount;
  }

  if (
    Boolean(values.isApplicableMultipleTimes) !==
    Boolean(originalValues.isApplicableMultipleTimes)
  ) {
    usage.is_applicable_multiple_times = Boolean(
      values.isApplicableMultipleTimes
    );
  }

  return Object.keys(usage).length > ZERO ? usage : undefined;
};

const buildCurrencyAmountsUpdatePayload = (values, originalValues) => {
  const nextSettingsByCurrency = values.currencySettings ?? {};
  const previousSettingsByCurrency = originalValues.currencySettings ?? {};
  const currencyIds = [
    ...new Set([
      ...Object.keys(nextSettingsByCurrency),
      ...Object.keys(previousSettingsByCurrency),
    ]),
  ];

  const isPercentage = isPercentageDiscount(originalValues.discountType);
  const updates = [];

  currencyIds.forEach((currencyId) => {
    const nextSettings = nextSettingsByCurrency[currencyId] ?? {};
    const previousSettings = previousSettingsByCurrency[currencyId] ?? {};
    const update = { currency_id: parseScopeIdForApi(currencyId) };
    let hasChanges = false;

    if (isPercentage) {
      const nextMaxAllowed = Number(nextSettings.maxAllowedAmount);
      const previousMaxAllowed = Number(previousSettings.maxAllowedAmount);
      if (
        !Number.isNaN(nextMaxAllowed) &&
        nextMaxAllowed !== previousMaxAllowed
      ) {
        update.max_allowed_discount_amount = toDecimalString(
          nextSettings.maxAllowedAmount
        );
        hasChanges = true;
      }
    }

    const nextMinimumPackageMrp = Number(nextSettings.minimumPackageMrp ?? ZERO);
    const previousMinimumPackageMrp = Number(
      previousSettings.minimumPackageMrp ?? ZERO
    );
    if (nextMinimumPackageMrp !== previousMinimumPackageMrp) {
      update.minimum_package_mrp = toDecimalString(
        nextSettings.minimumPackageMrp ?? ZERO
      );
      hasChanges = true;
    }

    const nextMinimumCartMrp = resolveMinimumCartMrpForApi(
      nextSettings.minimumCartMrp
    );
    const previousMinimumCartMrp = resolveMinimumCartMrpForApi(
      previousSettings.minimumCartMrp
    );
    if (nextMinimumCartMrp !== previousMinimumCartMrp) {
      update.minimum_cart_mrp = toDecimalString(nextMinimumCartMrp);
      hasChanges = true;
    }

    if (hasChanges) {
      updates.push(update);
    }
  });

  return updates.length > ZERO ? updates : undefined;
};

export const buildEditCouponPayload = (values, originalCoupon) => {
  const originalValues = mapCouponDetailsFromApi(originalCoupon);
  const payload = {
    remarks: (values.remarks ?? "").trim(),
    validity: {
      expiry_date: toApiDate(values.expiryDate),
    },
  };

  const usage = buildUsageUpdatePayload(values, originalValues);
  if (usage) {
    payload.usage = usage;
  }

  const currencyAmounts = buildCurrencyAmountsUpdatePayload(
    values,
    originalValues
  );
  if (currencyAmounts) {
    payload.currency_amounts = currencyAmounts;
  }

  const nextSelectAllPackages = Boolean(values.selectAllPackages);
  const previousSelectAllPackages = Boolean(originalValues.selectAllPackages);
  const nextPackageIds = parsePackageIds(values.packageIds);
  const previousPackageIds = parsePackageIds(originalValues.packageIds);

  if (
    nextSelectAllPackages !== previousSelectAllPackages ||
    nextPackageIds.join(",") !== previousPackageIds.join(",")
  ) {
    payload.applicable_packages = nextSelectAllPackages
      ? { select_all: true, package_ids: [] }
      : { select_all: false, package_ids: nextPackageIds };
  }

  const nextSelectAllPlatforms = Boolean(values.selectAllPlatforms);
  const previousSelectAllPlatforms = Boolean(originalValues.selectAllPlatforms);
  const nextPlatformIds = parsePackageIds(values.platformIds);
  const previousPlatformIds = parsePackageIds(originalValues.platformIds);

  if (
    nextSelectAllPlatforms !== previousSelectAllPlatforms ||
    nextPlatformIds.join(",") !== previousPlatformIds.join(",")
  ) {
    payload.applicable_platforms = nextSelectAllPlatforms
      ? { select_all: true, platform_ids: [] }
      : { select_all: false, platform_ids: nextPlatformIds };
  }

  return payload;
};
